import style from "./index.module.scss";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import AnalyticsByCompareOrderPrice from "../components/AnalyticsByCompareOrderPrice";
import BasicAnalytics from "../components/BasicAnalytics";
import AnalyticsByTransportType from "../components/AnalyticsByTransportType";
import { Analytics } from "../../../services";
import OrderCountByPeriod from "../components/OrderCountByPeriod";
import TopDirections from "../components/TopDirections";
import SLAComplianceByExpeditor from "../components/SLAComplianceByExpeditor";
import OrderCompletionPieChart from "../components/OrderCompletionPieChart";
import ExpeditorActivityChart from "../components/ExpeditorActivityChart";
import SLAComplianceByDriver from "../components/SLAComplianceByDriver";
import { useScreenSize } from "../../../hooks/useScreenSize";
import TopDirectionsMobile from "../components/TopDirectionsMobile";
import AnalyticsByTurnover from "../components/AnalyticsByTurnover";
import AnalyticsByClientOrders from "../components/AnalyticsByClientOrders";
import AnalyticsByClientPublicOrders from "../components/AnalyticsByClientPublicOrders";
import CreatedOrdersByEmployee from "../components/CreatedOrdersByEmployee";
import AppointmentMethodShare from "../components/AppointmentMethodShare";
import EmployeeOrdersByExpeditor from "../components/EmployeeOrdersByExpeditor";
import TopDirectionsTable from "../components/TopDirectionsTable";

const AnalyticsTransport = ({ user }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();

  const [baseMetricDateStart, setBaseMetricDateStart] = useState(moment().subtract(30, "days").toDate());
  const [baseMetricDateEnd, setBaseMetricDateEnd] = useState(new Date());
  const [currency, setCurrency] = useState("");

  const [baseMetric, setBaseMetric] = useState();
  const [orderCountByPeriod, setOrderCountByPeriod] = useState([]);
  const [topDirections, setTopDirections] = useState([]);
  const [monthlyOrderIndicators, setMonthlyOrderIndicators] = useState([]);
  const [chartСompareOrderPrice, setChartСompareOrderPrice] = useState([]);
  const [SLACompliance, setSLACompliance] = useState([]);
  const [SLAComplianceExpeditor, setSLAComplianceExpeditor] = useState([]);
  const [expeditorActivity, setExpeditorActivity] = useState([]);
  const [ontimeExpeditorOrdersCount, setOntimeExpeditorOrdersCount] = useState([]);
  const [turnover, setTurnover] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [publicOrders, setPublicOrders] = useState([]);
  const [employeeOrders, setEmployeeOrders] = useState([]);
  const [appointmentShareData, setAppointmentShareData] = useState([]);
  const [employeeOrdersByExpeditor, setEmployeeOrdersByExpeditor] = useState([]);
  const [top10DirectionsTable, setTop10DirectionsTable] = useState([]);

  const startDate = useMemo(() => moment(baseMetricDateStart).format("YYYY-MM-DD"), [baseMetricDateStart]);
  const endDate = useMemo(() => moment(baseMetricDateEnd).format("YYYY-MM-DD"), [baseMetricDateEnd]);

  const fetchAnalyticsData = async (apiFunc, setter, label = "") => {
    try {
      const currencyParam = currency === "all" || currency === "" ? "" : currency;
      const response = await apiFunc(startDate, endDate, currencyParam);
      setter(response.data);
    } catch (error) {
      console.error(`Error while getting ${label}:`, error);
    }
  };

  useEffect(() => {
    fetchAnalyticsData(Analytics.getAverageOrderIndicators, setBaseMetric, "base metrics");
    fetchAnalyticsData(Analytics.getMonthlyOrderIndicators, setMonthlyOrderIndicators, "monthly order indicators");
    fetchAnalyticsData(Analytics.compareOrderPriceIndicators, setChartСompareOrderPrice, "compare order price");
    fetchAnalyticsData(Analytics.getOrderCountByPeriod, setOrderCountByPeriod, "order count");
    fetchAnalyticsData(Analytics.getTopDirections, setTopDirections, "top directions");
    fetchAnalyticsData(Analytics.getExpeditorActivity, setExpeditorActivity, "expeditor activity");
    fetchAnalyticsData(Analytics.getTurnover, setTurnover, "turnover");
    fetchAnalyticsData(Analytics.getClientCreatedCompletedOrders, setCompletedOrders, "completed orders");
    fetchAnalyticsData(Analytics.getClientCreatedPublicOrders, setPublicOrders, "client created public orders");
    fetchAnalyticsData(Analytics.getClientEmployeesCreatedOrders, setEmployeeOrders, "client employees created orders");
    fetchAnalyticsData(Analytics.getClientEmployeeAppointmentShareByMethod, setAppointmentShareData, "employee share ofappointment");
    fetchAnalyticsData(Analytics.getEmployeeOrdersByExpeditor, setEmployeeOrdersByExpeditor, "employee orders by expeditor");
    fetchAnalyticsData(Analytics.getTop10Routes, setTop10DirectionsTable, "top 10 directions table")


    if (user.type === "Client") {
      fetchAnalyticsData(Analytics.getSLAComplianceClient, setSLACompliance, "SLA compliance for client");
    } else if (user.type === "Expeditor") {
      fetchAnalyticsData(Analytics.getSLAComplianceExpeditor, setSLAComplianceExpeditor, "SLA compliance for expeditor");
      fetchAnalyticsData(Analytics.getOntimeExpeditorOrdersCount, setOntimeExpeditorOrdersCount, "Count order ontime expeditor");
    }
  }, [startDate, endDate, user.type, currency]);

  return (
    <div className={style.wrapperAnalytics}>
      <BasicAnalytics
        startDate={baseMetricDateStart}
        setStartDate={setBaseMetricDateStart}
        endDate={baseMetricDateEnd}
        setEndtDate={setBaseMetricDateEnd}
        item={baseMetric}
        currency={currency} 
        setCurrency={setCurrency} 
      />

      <div className={style.metric}>
        <AnalyticsByCompareOrderPrice item={chartСompareOrderPrice} currency={currency} />
        <AnalyticsByTransportType item={monthlyOrderIndicators} />
      </div>

      <div className={style.metric}>
        <AnalyticsByTurnover item={turnover} currency={currency} />
        <OrderCountByPeriod item={orderCountByPeriod} />
      </div>

      <div className={style.metric}>
        <AnalyticsByClientOrders item={completedOrders} />

        {isMobileView ? <TopDirectionsMobile item={topDirections} /> : <TopDirections item={topDirections} />}
      </div>

      <div className={style.metric}>
        <AnalyticsByClientPublicOrders item={publicOrders} />
        <CreatedOrdersByEmployee item={employeeOrders} />
      </div>

      <div className={style.metric}>
        <ExpeditorActivityChart item={expeditorActivity} />
        <AppointmentMethodShare item={appointmentShareData}/>
      </div>

      <div className={style.metric}>
        <SLAComplianceByExpeditor item={SLACompliance} />
        <EmployeeOrdersByExpeditor item={employeeOrdersByExpeditor} />
      </div>

      {user.type !== "Client" && (
        <div className={style.metric}>
          <SLAComplianceByDriver item={SLAComplianceExpeditor} />
          <OrderCompletionPieChart item={ontimeExpeditorOrdersCount} />
        </div>
      )}

      <TopDirectionsTable 
        top10DirectionsTable={top10DirectionsTable} 
        currency={currency}
      />
    </div>
  );
};

export default AnalyticsTransport;
